import Combobox from "@/components/Combobox";
import InputError from "@/components/InputError";
import SkeletonRow from "@/components/SkeletonRow";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  listAkreditasiInternasional,
  listHubunganKeluarga, // Ensure this exists in your constants
  listLokasiKuliah,
  listSize,
} from "@/constants";
import useFilePreview from "@/hooks/useFilePreview";
import useSessionStorage from "@/hooks/useSessionStorage";
import useTimePeriode from "@/hooks/useTimePeriode";
import { useUser } from "@/hooks/useUser";
import { formatDate } from "@/lib/utils";
import {
  getBiodata,
  getListAgama,
  getListDataPilihanProdi,
  getListNegara,
} from "@/services/getData";
import {
  Agama,
  BiodataSate,
  DataPendaftaran,
  DataSiswa,
  HubunganKeluargaEnum, // Ensure this exists in your types
  LokasiKuliah,
  Negara,
  Option,
  Size,
} from "@/types";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import {
  Briefcase,
  Camera,
  Contact2,
  FileText,
  GraduationCap,
  MapPin,
  Ruler,
  User,
} from "lucide-react";
import { SetStateAction, useEffect, useRef, useState } from "react";
import { FaPlus } from "react-icons/fa6";
import PhoneInputWithCountrySelect from "react-phone-number-input";

// --- REUSABLE WRAPPER COMPONENT ---
const FormSection = ({
  icon,
  title,
  children,
  className = "",
}: {
  icon: React.ReactNode;
  title: string;
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={`rounded-xl border border-slate-200 bg-white p-5 shadow-sm dark:border-slate-800 dark:bg-slate-900 ${className}`}
    >
      <h3 className="mb-4 flex items-center gap-2 border-b pb-2 font-bold text-slate-800 dark:text-slate-200">
        {icon} {title}
      </h3>
      {children}
    </div>
  );
};

export default function Biodata({
  form,
  setForm,
  error,
  file,
  setFile,
  setParentProofOfIncomeFile,
}: {
  form: BiodataSate;
  setForm: React.Dispatch<SetStateAction<BiodataSate>>;
  error: AxiosError<{
    message?: string;
    errors?: Record<string, string[]>;
  }> | null;
  file: File | null;
  setFile: React.Dispatch<SetStateAction<File | null>>;
  parentProofOfIncomeFile?: File | null;
  setParentProofOfIncomeFile?: React.Dispatch<SetStateAction<File | null>>;
}) {
  const { user, isPending: isPendingUser } = useUser();
  const { dataPeriode } = useTimePeriode();
  const preview = useFilePreview(file);

  const [sessionPeriode] = useSessionStorage("sessionPeriode", {
    id_sys_periode: "",
  });

  const uploadRef = useRef<HTMLInputElement | null>(null);
  const [openNegara, setOpenNegara] = useState(false);

  const { data: dataPilihanProdi } = useQuery({
    queryKey: ["listDataPilihanProdi"],
    queryFn: () => getListDataPilihanProdi(sessionPeriode?.id_sys_periode),
  });
  const jenjangPilihanProdi = dataPilihanProdi
    ? dataPilihanProdi[0]?.unitkerja?.uk_program
    : "";

  const isShowAsalSekolah =
    jenjangPilihanProdi == "S1" ||
    jenjangPilihanProdi == "D3" ||
    jenjangPilihanProdi == "D4";

  const { data: dataNegara } = useQuery({
    queryKey: ["listNegara"],
    queryFn: () => getListNegara(),
    select: (data) =>
      data?.map((negara: Negara) => ({
        label: negara.nama_negara,
        value: `${negara.id_ref_negara}`,
      })),
  });

  const { data: dataAgama } = useQuery({
    queryKey: ["listAgama"],
    queryFn: () => getListAgama(),
  });

  const { data: dataSiswa, isPending: isPendingDataSiswa } =
    useQuery<DataSiswa>({
      queryKey: ["dataBiodata", user?.id, sessionPeriode?.id_sys_periode],
      queryFn: () => getBiodata(sessionPeriode?.id_sys_periode || ""),
      enabled: !!user && !!sessionPeriode?.id_sys_periode,
    });
  const dataPendaftaran: DataPendaftaran | undefined =
    dataSiswa?.data_pendaftaran?.[0] || undefined;

  useEffect(() => {
    if (!dataSiswa || !sessionPeriode?.id_sys_periode) return;

    setForm((prev) => ({
      ...prev,
      id_sys_periode: sessionPeriode?.id_sys_periode || "",
      ...dataSiswa,
      pilihan_lokasi_kuliah:
        (dataSiswa.data_pendaftaran?.[0]?.data_pilihan_prodi?.[0]?.pilihan_lokasi_kuliah?.toLowerCase() as LokasiKuliah) ??
        "",
      asal_sekolah: dataPendaftaran?.asal_sekolah,
      asal_jurusan_sekolah_internasional:
        dataPendaftaran?.asal_jurusan_sekolah_internasional,
      asal_pt: dataPendaftaran?.asal_pt,
      akreditasi_pt_asal: dataPendaftaran?.akreditasi_pt_asal,
      accrediting_organization_pt: dataPendaftaran?.accrediting_organization_pt,
      asal_prodi: dataPendaftaran?.asal_prodi,
      akreditasi_prodi_asal: dataPendaftaran?.akreditasi_prodi_asal,
      accrediting_organization_prodi:
        dataPendaftaran?.accrediting_organization_prodi,
      tahun_ijazah_terakhir: dataPendaftaran?.tahun_ijazah_terakhir,
      nilai_IPK: dataPendaftaran?.nilai_IPK,
      is_bekerja: Number(dataPendaftaran?.is_bekerja) === 1 ? true : false,
      tempat_bekerja: dataPendaftaran?.tempat_bekerja,
      penghasilan_bekerja: dataPendaftaran?.penghasilan_bekerja,
      kontak_darurat_hp: dataSiswa?.kontak_darurat_hp,
      kontak_darurat_hubungan: dataSiswa?.kontak_darurat_hubungan,
      kontak_darurat_nama: dataSiswa?.kontak_darurat_nama,
      is_applying_scholarship:
        dataPendaftaran?.is_applying_scholarship !== undefined &&
        dataPendaftaran?.is_applying_scholarship !== null
          ? Number(dataPendaftaran.is_applying_scholarship) === 1
          : undefined,
      parent_proof_of_income_path: dataPendaftaran?.parent_proof_of_income_path,
    }));
  }, [dataSiswa, dataPendaftaran, setForm, sessionPeriode?.id_sys_periode]);

  console.log({ dataPendaftaran });

  const isKelasDenpasar =
    dataSiswa?.data_pendaftaran?.[0]?.periode?.sys_prodi?.[0]
      ?.is_kelas_denpasar ?? false;

  const isIndonesia = form.id_ref_negara === "103";

  return (
    <div>
      <h1 className="mb-5 font-bold text-xl">Personal Information</h1>
      {isPendingUser || isPendingDataSiswa ? (
        <div>
          <section className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
            {Array(16)
              .fill(null)
              .map((_, index) => {
                return <SkeletonRow key={index} />;
              })}
          </section>
        </div>
      ) : (
        <div className="flex flex-col gap-6">
          {/* --- PHOTO UPLOAD SECTION --- */}
          <div className="flex flex-col items-center gap-6 rounded-xl border border-slate-200 bg-white p-6 shadow-sm dark:border-slate-800 dark:bg-slate-900 sm:flex-row">
            <div
              className="group relative flex h-[160px] w-[120px] shrink-0 cursor-pointer flex-col items-center justify-center overflow-hidden rounded-lg border-2 border-dashed border-slate-300 bg-slate-50 transition-all hover:border-blue-500 dark:border-slate-700 dark:bg-slate-950"
              onClick={() => {
                if (uploadRef.current) {
                  uploadRef.current.click();
                }
              }}
            >
              {preview || form.path_foto ? (
                <>
                  <img
                    src={preview || form.path_foto || ""}
                    alt="Profile Preview"
                    className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-105"
                  />
                  {/* Overlay on Hover */}
                  <div className="absolute inset-0 flex items-center justify-center bg-black/40 opacity-0 transition-opacity duration-300 group-hover:opacity-100">
                    <FaPlus className="text-2xl text-white" />
                  </div>
                </>
              ) : (
                <div className="flex h-full flex-col items-center justify-center text-slate-400">
                  <Camera className="mb-2 h-8 w-8 opacity-50" />
                  <span className="px-2 text-center text-xs font-medium">
                    Upload Photo
                  </span>
                </div>
              )}
            </div>

            <div className="flex-1 space-y-2 text-center sm:text-left">
              <h3 className="text-lg font-bold text-slate-800 dark:text-slate-200">
                Profile Photo
              </h3>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                Please upload your latest formal photo. The face must be clearly
                visible facing forward.
                <br className="hidden sm:block" />
                Supported formats: JPG, JPEG, PNG. Max size: 1MB.
              </p>

              {/* File Name Display */}
              <div className="text-xs font-medium text-blue-600 dark:text-blue-400">
                {file && <p>Selected File: {file.name}</p>}
              </div>

              <InputError
                text={error?.response?.data?.errors?.foto?.join(",")}
              />
            </div>

            <input
              ref={uploadRef}
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              className="hidden"
              onChange={(event) => {
                const files = event.target.files;
                if (files && files[0]) {
                  const file = files[0];
                  setFile(file);
                }
              }}
            />
          </div>

          {/* --- MAIN INFORMATION SECTION --- */}
          <FormSection
            icon={<User className="w-4 h-4 text-blue-600" />}
            title="Main Information"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              {isKelasDenpasar ? (
                <div>
                  <Label>Lecture Location</Label>
                  <Select
                    value={form.pilihan_lokasi_kuliah || ""}
                    onValueChange={(value) => {
                      setForm((prev) => ({
                        ...prev,
                        pilihan_lokasi_kuliah: value as LokasiKuliah,
                      }));
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={"Pilih Lokasi Kuliah"}>
                        {form.pilihan_lokasi_kuliah &&
                          listLokasiKuliah?.find(
                            (value) => value === form.pilihan_lokasi_kuliah,
                          )}
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {listLokasiKuliah?.map((value, index: number) => (
                        <SelectItem
                          key={index}
                          value={`${value.toLowerCase()}`}
                        >
                          {value}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <InputError
                    text={error?.response?.data?.errors?.pilihan_lokasi_kuliah?.join(
                      ",",
                    )}
                  />
                </div>
              ) : (
                ""
              )}
              <div>
                <Label required>Full Name (Without Title)</Label>
                <Input
                  required
                  value={form.nama_lengkap || ""}
                  placeholder="type Full Name (Without Title)..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      nama_lengkap: event.target.value,
                    }))
                  }
                />
                <p className="text-muted-foreground text-sm">
                  Nama lengkap harus sesuai dengan ijazah terakhir, tanpa gelar!
                </p>
                <InputError
                  text={error?.response?.data?.errors?.nama_lengkap?.join(",")}
                />
              </div>
              <div>
                <Label required>Email</Label>
                <Input readOnly disabled value={dataSiswa?.email || ""} />
              </div>
              <div>
                <Label required>Passport Number</Label>
                <Input readOnly disabled value={dataSiswa?.nik || ""} />
              </div>

              <div>
                <Label required>Date of Birth</Label>
                <Input
                  required
                  type="date"
                  value={form.tanggal_lahir || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tanggal_lahir: event.target.value,
                    }))
                  }
                />
                <p className="text-primary font-semibold">
                  {form.tanggal_lahir &&
                    formatDate(form.tanggal_lahir, "medium", "en-US")}
                </p>
                <InputError
                  text={error?.response?.data?.errors?.tanggal_lahir?.join(",")}
                />
              </div>
              <div>
                <Label required>Place of Birth</Label>
                <Input
                  required
                  value={form.tempat_lahir || ""}
                  placeholder="type Place of Birth..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tempat_lahir: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.tempat_lahir?.join(",")}
                />
              </div>

              <div>
                <Label>Phone Number</Label>
                {isIndonesia ? (
                  <Input
                    required
                    placeholder={`type Phone number...`}
                    value={form.handphone || ""}
                    pattern="\d*"
                    inputMode="numeric"
                    onInput={(event) => {
                      event.currentTarget.value =
                        event.currentTarget.value.replace(/\D/g, "");
                    }}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        handphone: event.target.value.replace(/\D/g, ""),
                      }))
                    }
                  />
                ) : (
                  <PhoneInputWithCountrySelect
                    placeholder="Enter phone number"
                    value={form.handphone || ""}
                    onChange={(newValue) => {
                      setForm((prev) => ({
                        ...prev,
                        handphone: newValue || "",
                      }));
                    }}
                    className="bg-secondary rounded-full py-2 px-3"
                  />
                )}

                <InputError
                  text={error?.response?.data?.errors?.handphone?.join(",")}
                />
              </div>

              <div>
                <Label required>Religion</Label>
                <Select
                  value={form.id_ref_agama || ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      id_ref_agama: value,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Choose Religion"}>
                      {form.id_ref_agama &&
                        dataAgama?.find(
                          (agama: Agama) =>
                            agama.id_ref_agama == String(form.id_ref_agama),
                        )?.nama_agama_en}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {dataAgama?.map((agama: Agama, index: number) => (
                      <SelectItem key={index} value={`${agama.id_ref_agama}`}>
                        {agama.nama_agama_en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.id_ref_agama?.join(",")}
                />
              </div>

              <div>
                <Label required>Gender</Label>
                <Select
                  value={form.jenis_kelamin || ""}
                  onValueChange={(value) => {
                    setForm((prev) => ({
                      ...prev,
                      jenis_kelamin: value as "L" | "P",
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Choose Gender"}>
                      {form.jenis_kelamin &&
                        [
                          { label: "Male", value: "L" },
                          { label: "Female", value: "P" },
                        ]?.find((option) => option.value === form.jenis_kelamin)
                          ?.label}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {[
                      { label: "Male", value: "L" },
                      { label: "Female", value: "P" },
                    ]?.map((option, index) => (
                      <SelectItem key={index} value={`${option.value}`}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.jenis_kelamin?.join(",")}
                />
              </div>

              <div>
                <Label required>Mother’s Full Name</Label>
                <Input
                  required
                  value={form.nama_ibu_kandung || ""}
                  placeholder="type Mother’s Full Name..."
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      nama_ibu_kandung: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.nama_ibu_kandung?.join(
                    ",",
                  )}
                />
              </div>
            </div>
          </FormSection>

          {/* --- PHYSICAL & NATIONALITY SECTION --- */}
          <FormSection
            icon={<Ruler className="w-4 h-4 text-purple-600" />}
            title="Physical & Nationality"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              <div>
                <Label required>Jacket Size</Label>
                <Select
                  value={form.ukuran_baju || ""}
                  onValueChange={(value: string) => {
                    setForm((prev) => ({
                      ...prev,
                      ukuran_baju: value as Size,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={"Choose Jacket Size"}>
                      {form.ukuran_baju &&
                        listSize?.find((value) => value === form.ukuran_baju)}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {listSize?.map((value, index) => (
                      <SelectItem key={index} value={`${value}`}>
                        {value}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.ukuran_baju?.join(",")}
                />
              </div>

              <div>
                <Label required>Nationality</Label>
                <Combobox
                  open={openNegara}
                  setOpen={setOpenNegara}
                  value={form.id_ref_negara || ""}
                  setValue={(newValue) =>
                    setForm((prev) => ({
                      ...prev,
                      id_ref_negara: newValue,
                    }))
                  }
                  options={dataNegara}
                  placeholder="Choose Nationality"
                />
                <InputError
                  text={error?.response?.data?.errors?.id_ref_negara?.join(",")}
                />
              </div>
            </div>
          </FormSection>

          {/* --- ADDRESS SECTION --- */}
          <FormSection
            icon={<MapPin className="w-4 h-4 text-emerald-600" />}
            title="Address"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              <div>
                <Label required>Permanent Address</Label>
                <Input
                  required
                  placeholder="type Permanent Address..."
                  value={form.jalan || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      jalan: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.jalan?.join(",")}
                />
              </div>

              <div>
                <Label required>Current Address</Label>
                <Input
                  required
                  placeholder="type Current Address..."
                  value={form.jalan_domisili || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      jalan_domisili: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.jalan?.join(",")}
                />
              </div>

              <div>
                <Label required>State/Province/Region</Label>
                <Input
                  placeholder="Type State/Province/Region..."
                  value={form.dusun || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      dusun: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.dusun?.join(",")}
                />
              </div>

              <div>
                <Label required>City/Town</Label>
                <Input
                  placeholder="Type city/town..."
                  value={form.kelurahan || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      kelurahan: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kelurahan?.join(",")}
                />
              </div>

              <div>
                <Label required>Postal Code</Label>
                <Input
                  placeholder="type Postal Code..."
                  value={form.kode_pos || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      kode_pos: event.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kode_pos?.join(",")}
                />
              </div>
            </div>
          </FormSection>

          {/* --- EMERGENCY CONTACT SECTION --- */}
          <FormSection
            icon={<Contact2 className="w-4 h-4 text-amber-600" />}
            title="Emergency Contact"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              <div className="lg:col-span-2">
                <Label required>Relationship</Label>
                <Select
                  value={form.kontak_darurat_hubungan || ""}
                  onValueChange={(value) => {
                    if (!value) return;
                    setForm((prev) => ({
                      ...prev,
                      kontak_darurat_hubungan: value as HubunganKeluargaEnum,
                    }));
                  }}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Choose Relationship" />
                  </SelectTrigger>
                  <SelectContent>
                    {listHubunganKeluarga?.map(
                      (option: Option, index: number) => (
                        <SelectItem key={index} value={option.value}>
                          {option.labelEn}
                        </SelectItem>
                      ),
                    )}
                  </SelectContent>
                </Select>
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_hubungan?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Contact Name</Label>
                <Input
                  required
                  placeholder="Type contact name..."
                  value={form.kontak_darurat_nama || ""}
                  onChange={(e) =>
                    setForm((prev) => ({
                      ...prev,
                      kontak_darurat_nama: e.target.value,
                    }))
                  }
                />
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_nama?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>Phone Number</Label>
                <Input
                  required
                  maxLength={15}
                  value={form.kontak_darurat_hp || ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      kontak_darurat_hp: e.target.value.replace(/\D/g, ""),
                    })
                  }
                  placeholder="08xxxxxxxxxx"
                  inputMode="numeric"
                />
                <InputError
                  text={error?.response?.data?.errors?.kontak_darurat_hp?.join(
                    ",",
                  )}
                />
              </div>
            </div>
          </FormSection>

          {/* --- EDUCATION SECTION --- */}
          <FormSection
            icon={<GraduationCap className="w-4 h-4 text-orange-600" />}
            title="Education"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              {isShowAsalSekolah ? (
                <>
                  <div>
                    <Label required>Previous School</Label>
                    <Input
                      required
                      value={form.asal_sekolah || ""}
                      placeholder="type previous school..."
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          asal_sekolah: event.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_sekolah?.join(
                        ",",
                      )}
                    />
                  </div>

                  <div>
                    <Label required>
                      Main Subjects or Focus Area in Secondary School
                    </Label>
                    <Input
                      required
                      value={form.asal_jurusan_sekolah_internasional || ""}
                      placeholder="Type your School Major/Field of Expertise..."
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          asal_jurusan_sekolah_internasional:
                            event.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_jurusan_sekolah_internasional?.join(
                        ",",
                      )}
                    />
                  </div>
                </>
              ) : (
                <>
                  <div>
                    <Label required>
                      {jenjangPilihanProdi == "S3" &&
                        "University/Institution of Master’s Degree"}
                      {jenjangPilihanProdi == "S2" &&
                        "University/Institution of Bachelor's Degree"}
                    </Label>
                    <Input
                      required
                      placeholder="type University/Institution..."
                      value={form.asal_pt || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          asal_pt: event.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_pt?.join(",")}
                    />
                  </div>

                  <div>
                    <Label required>
                      {jenjangPilihanProdi == "S3" &&
                        "Accreditation Rating (Master’s Degree University or Institution)"}
                      {jenjangPilihanProdi == "S2" &&
                        "Accreditation Rating (Bachelor's Degree University or Institution)"}
                    </Label>
                    <Select
                      value={form.akreditasi_pt_asal || ""}
                      onValueChange={(value: string) => {
                        setForm((prev) => ({
                          ...prev,
                          akreditasi_pt_asal: value,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            jenjangPilihanProdi === "S3"
                              ? "Select accreditation rating for Master's Degree institution"
                              : jenjangPilihanProdi === "S2"
                                ? "Select accreditation rating for Bachelor's Degree institution"
                                : "Select accreditation rating"
                          }
                        >
                          {form.akreditasi_pt_asal &&
                            listAkreditasiInternasional?.find(
                              (value) => value === form.akreditasi_pt_asal,
                            )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {listAkreditasiInternasional?.map((value, index) => (
                          <SelectItem key={index} value={`${value}`}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.akreditasi_pt_asal?.join(
                        ",",
                      )}
                    />
                  </div>

                  {form.akreditasi_pt_asal == "Accredited" ? (
                    <div>
                      <Label required>Accrediting Agency</Label>
                      <Input
                        required
                        placeholder="type Accrediting Agency..."
                        value={form.accrediting_organization_pt || ""}
                        onChange={(event) =>
                          setForm((prev) => ({
                            ...prev,
                            accrediting_organization_pt: event.target.value,
                          }))
                        }
                      />
                      <InputError
                        text={error?.response?.data?.errors?.accrediting_organization_pt?.join(
                          ",",
                        )}
                      />
                    </div>
                  ) : (
                    ""
                  )}

                  <div>
                    <Label required>
                      {jenjangPilihanProdi == "S3" &&
                        "Field of Study (Master’s Degree)"}
                      {jenjangPilihanProdi == "S2" &&
                        "Field of Study (Bachelor's Degree)"}
                    </Label>
                    <Input
                      required
                      placeholder="type Major / Study Program..."
                      value={form.asal_prodi || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          asal_prodi: event.target.value,
                        }))
                      }
                    />
                    <InputError
                      text={error?.response?.data?.errors?.asal_prodi?.join(
                        ",",
                      )}
                    />
                  </div>

                  <div>
                    <Label required>
                      {jenjangPilihanProdi == "S3" &&
                        "Master’s Program Accreditation Rating"}
                      {jenjangPilihanProdi == "S2" &&
                        "Bachelor's Program Accreditation Rating"}
                    </Label>
                    <Select
                      value={form.akreditasi_prodi_asal || ""}
                      onValueChange={(value: string) => {
                        setForm((prev) => ({
                          ...prev,
                          akreditasi_prodi_asal: value,
                        }));
                      }}
                    >
                      <SelectTrigger>
                        <SelectValue
                          placeholder={
                            jenjangPilihanProdi === "S3"
                              ? "Select Master’s Program Accreditation Rating"
                              : jenjangPilihanProdi === "S2"
                                ? "Select Bachelor's Program Accreditation Rating"
                                : "Select Accreditation Rating"
                          }
                        >
                          {form.akreditasi_prodi_asal &&
                            listAkreditasiInternasional?.find(
                              (value) => value === form.akreditasi_prodi_asal,
                            )}
                        </SelectValue>
                      </SelectTrigger>
                      <SelectContent>
                        {listAkreditasiInternasional?.map((value, index) => (
                          <SelectItem key={index} value={`${value}`}>
                            {value}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <InputError
                      text={error?.response?.data?.errors?.akreditasi_prodi_asal?.join(
                        ",",
                      )}
                    />
                  </div>
                </>
              )}

              {form.akreditasi_prodi_asal == "Accredited" ? (
                <div>
                  <Label required>Accrediting Agency</Label>
                  <Input
                    required
                    placeholder="type Accrediting Agency..."
                    value={form.accrediting_organization_prodi || ""}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        accrediting_organization_prodi: event.target.value,
                      }))
                    }
                  />
                  <InputError
                    text={error?.response?.data?.errors?.accrediting_organization_prodi?.join(
                      ",",
                    )}
                  />
                </div>
              ) : (
                ""
              )}

              <div>
                <Label required>Year of Last Diploma</Label>
                <Input
                  minLength={4}
                  maxLength={4}
                  value={form.tahun_ijazah_terakhir || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      tahun_ijazah_terakhir: event.target.value,
                    }))
                  }
                  placeholder="type Year of Last Diploma..."
                />
                <InputError
                  text={error?.response?.data?.errors?.tahun_ijazah_terakhir?.join(
                    ",",
                  )}
                />
              </div>

              <div>
                <Label required>GPA Score</Label>
                <Input
                  type="number"
                  value={form.nilai_IPK || ""}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      nilai_IPK: event.target.value,
                    }))
                  }
                  placeholder="Type your GPA score..."
                />
                <InputError
                  text={error?.response?.data?.errors?.nilai_IPK?.join(",")}
                />
              </div>
            </div>
          </FormSection>

          {/* --- EMPLOYMENT SECTION --- */}
          <FormSection
            icon={<Briefcase className="w-4 h-4 text-slate-600" />}
            title="Employment"
          >
            <div className="grid lg:grid-cols-2 grid-cols-1 gap-y-3 gap-x-5">
              <div>
                <Label required>Employment Status</Label>
                <div className="mt-1 flex gap-5">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.is_bekerja === false}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: checked ? false : undefined,
                        }))
                      }
                    />
                    <Label>Unemployed</Label>
                  </div>
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={form.is_bekerja === true}
                      onCheckedChange={(checked) =>
                        setForm((prev) => ({
                          ...prev,
                          is_bekerja: checked ? true : undefined,
                        }))
                      }
                    />
                    <Label>Employed</Label>
                  </div>
                </div>
                <InputError
                  text={error?.response?.data?.errors?.is_bekerja?.join(",")}
                />
              </div>

              {form.is_bekerja ? (
                <>
                  <div>
                    <Label required>Institution</Label>
                    <Input
                      required
                      value={form.tempat_bekerja || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          tempat_bekerja: event.target.value,
                        }))
                      }
                      placeholder="type Institution..."
                    />
                    <InputError
                      text={error?.response?.data?.errors?.tempat_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                  <div>
                    <Label required>Monthly Gross Income</Label>
                    <Input
                      required
                      type="number"
                      value={form.penghasilan_bekerja || ""}
                      onChange={(event) =>
                        setForm((prev) => ({
                          ...prev,
                          penghasilan_bekerja: event.target.value,
                        }))
                      }
                      placeholder="type Monthly Gross Income..."
                    />
                    <InputError
                      text={error?.response?.data?.errors?.penghasilan_bekerja?.join(
                        ",",
                      )}
                    />
                  </div>
                </>
              ) : (
                ""
              )}
            </div>
          </FormSection>

          {/* --- SCHOLARSHIP SECTION --- */}
          {(dataPeriode as any)?.is_internasional_kerjasama == 0 ? (
            <FormSection
              icon={<FileText className="w-4 h-4 text-pink-600" />}
              title="Undiksha Scholarship Application"
            >
              <div className="grid grid-cols-1 gap-y-3 gap-x-5">
                <div>
                  <Label required>
                    Would you like to apply for the Undiksha Scholarship?
                  </Label>
                  <div className="mt-1 flex gap-5">
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="scholarship_yes"
                        checked={form.is_applying_scholarship === true}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({
                            ...prev,
                            is_applying_scholarship: checked ? true : undefined,
                          }))
                        }
                      />
                      <Label
                        htmlFor="scholarship_yes"
                        className="cursor-pointer"
                      >
                        Yes
                      </Label>
                    </div>
                    <div className="flex items-center gap-3">
                      <Checkbox
                        id="scholarship_no"
                        checked={form.is_applying_scholarship === false}
                        onCheckedChange={(checked) =>
                          setForm((prev) => ({
                            ...prev,
                            is_applying_scholarship: checked
                              ? false
                              : undefined,
                          }))
                        }
                      />
                      <Label
                        htmlFor="scholarship_no"
                        className="cursor-pointer"
                      >
                        No
                      </Label>
                    </div>
                  </div>
                  <InputError
                    text={error?.response?.data?.errors?.is_applying_scholarship?.join(
                      ",",
                    )}
                  />
                </div>

                {form.is_applying_scholarship === true && (
                  <div className="mt-2">
                    <Label required>
                      Upload your parents' or your monthly earnings issued by
                      official institution.
                    </Label>
                    <div className="mt-1 flex items-center gap-3">
                      <Input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(event) => {
                          const files = event.target.files;
                          if (files && files[0]) {
                            setParentProofOfIncomeFile?.(files[0]);
                          }
                        }}
                      />
                    </div>
                    {dataPendaftaran?.parent_proof_of_income_path && (
                      <p className="text-xs text-muted-foreground mt-1">
                        Current file:{" "}
                        <a
                          href={dataPendaftaran?.parent_proof_of_income_path}
                          target="_blank"
                          rel="noreferrer"
                          className="text-blue-500 underline"
                        >
                          View Document
                        </a>
                      </p>
                    )}
                    <InputError
                      text={error?.response?.data?.errors?.parent_proof_of_income_path?.join(
                        ",",
                      )}
                    />
                  </div>
                )}
              </div>
            </FormSection>
          ) : null}
        </div>
      )}
    </div>
  );
}
